import React, { useState, useEffect } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const Container = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
  width: '100%',
}));

// Reserves space for the question image so the question text and answer
// buttons don't jump on first load. min-height keeps the slot at a stable
// height while the bundled WebP fetches; the image itself caps at maxHeight.
const ImageSlot = styled('div')(({ theme }) => ({
  position: 'relative',
  width: '100%',
  minHeight: 240,
  margin: '0 auto 16px',
  borderRadius: 12,
  backgroundColor: '#fff',
  border: `1px solid ${theme.palette.grey[200]}`,
  overflow: 'hidden',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const shimmer = keyframes({
  '0%':   { backgroundPosition: '-200% 0' },
  '100%': { backgroundPosition: '200% 0' },
});

const Shimmer = styled('div')({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%), #eeeeee',
  backgroundSize: '200% 100%',
  animation: `${shimmer} 1.4s ease-in-out infinite`,
});

const QuestionImage = styled('img', {
  shouldForwardProp: (prop) => prop !== 'loaded',
})(({ loaded }) => ({
  display: 'block',
  maxWidth: '100%',
  maxHeight: 320,
  width: 'auto',
  height: 'auto',
  borderRadius: 12,
  backgroundColor: '#fff',
  position: 'relative',
  zIndex: 1,
  opacity: loaded ? 1 : 0,
  transition: 'opacity 200ms ease-out',
}));

function WonderlicImage({ src, alt }) {
  const [loaded, setLoaded] = useState(false);
  // Reset on src change so navigating between questions re-shows the skeleton.
  useEffect(() => { setLoaded(false); }, [src]);
  return (
    <ImageSlot>
      {!loaded && <Shimmer aria-hidden="true" />}
      <QuestionImage
        src={src}
        alt={alt}
        loaded={loaded}
        onLoad={() => setLoaded(true)}
        // If decode fails, still drop the shimmer so the user can move on.
        onError={() => setLoaded(true)}
      />
    </ImageSlot>
  );
}

const QuestionText = styled(Typography)(({ theme }) => ({
  fontSize: 20,
  fontWeight: 500,
  marginBottom: 16,
  lineHeight: 1.3,
  whiteSpace: 'pre-line',
}));

const PAIR_RE = /\d+\s+[—–-]\s+\d+/g;

function formatNumberPairs(text) {
  if (typeof text !== 'string') return text;
  const matches = text.match(PAIR_RE);
  if (!matches || matches.length < 2) return text;
  const firstIdx = text.search(PAIR_RE);
  const prompt = text.slice(0, firstIdx).trimEnd();
  return prompt ? `${prompt}\n${matches.join('\n')}` : matches.join('\n');
}

const OptionsList = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
}));

const OptionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ theme, isSelected }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '12px 16px',
  borderRadius: 16,
  border: isSelected ? `2px solid ${theme.palette.primary.main}` : '2px solid #e0e0e0',
  backgroundColor: '#fff',
  textTransform: 'none',
  width: '100%',
  transition: 'border-color 120ms ease',
  '&:hover': {
    backgroundColor: '#fff',
    borderColor: isSelected ? theme.palette.primary.main : '#bdbdbd',
  },
}));

const OptionNumber = styled('div', {
  shouldForwardProp: (prop) => prop !== 'isSelected',
})(({ theme, isSelected }) => ({
  width: 28,
  height: 28,
  borderRadius: '50%',
  backgroundColor: isSelected ? theme.palette.primary.main : '#f0f0f0',
  color: isSelected ? '#fff' : theme.palette.grey[500],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 14,
  fontWeight: 600,
  marginRight: 12,
  flexShrink: 0,
}));

const OptionText = styled(Typography)(({ theme, isSelected }) => ({
  fontSize: 14,
  color: isSelected ? theme.palette.primary.main : theme.palette.grey[700],
  textAlign: 'left',
  flex: 1,
}));

const CheckIcon = styled('div')(({ theme }) => ({
  width: 20,
  height: 20,
  borderRadius: '50%',
  border: '2px solid',
  marginLeft: 12,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 12,
}));

export default function MultiChoice({
  question,
  options,
  selectedId,
  onSelect,
  showNumbers = false,
  imageSrc,
  imageAlt,
}) {
  return (
    <Container>
      {imageSrc && <WonderlicImage src={imageSrc} alt={imageAlt || ''} />}
      <QuestionText>{formatNumberPairs(question)}</QuestionText>
      <OptionsList>
        {options.map((option, index) => {
          const isSelected = selectedId === (option.id ?? index);
          return (
            <OptionButton
              key={option.id ?? index}
              isSelected={isSelected}
              onClick={() => onSelect(option.id ?? index)}
            >
              {showNumbers && (
                <OptionNumber isSelected={isSelected}>
                  {index + 1}
                </OptionNumber>
              )}
              <OptionText isSelected={isSelected}>
                {option.text || option}
              </OptionText>
              <CheckIcon
                style={{
                  borderColor: isSelected ? '#79c912' : '#bdbdbd',
                  backgroundColor: isSelected ? '#79c912' : 'transparent',
                  color: isSelected ? '#fff' : 'transparent',
                }}
              >
                ✓
              </CheckIcon>
            </OptionButton>
          );
        })}
      </OptionsList>
    </Container>
  );
}
